import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useApiCall } from '@/hooks/useApiCall';
import { assetService } from '../api/assets/asset.service';
import { AssetModel } from '../types/AssetModel';
import { PagePaginationResultDto, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { AssetSortOption } from '../api/assets/asset.contracts';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import { FilterValues, FilterFieldName } from '@/components/filterbar/FilterBarContext';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { GridApi, SortChangedEvent, GridReadyEvent } from 'ag-grid-community';
import dayjs from 'dayjs';
import { TrackerType } from '@/modules/trackers';
import { trackerTypeToString } from '@/modules/trackers/lib/tracker.utils';
import { Button } from '@mui/material';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { ActionBar } from '@/modules/application';
import { ActionBarDeleteButton } from '@/modules/application/components/ActionBarDeleteButton';
import { MoveAssetsToLocationModal } from '@/modules/locations/components/MoveAssetsToLocationModal';
import { toast } from 'react-toastify';
import PaginationControls from '@/components/grid/PaginationControls';
import PagedResultDataText from '@/components/filterbar/PagedResultDataText';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import FilterBar from '@/components/filterbar/FilterBar';
import TextFilter from '@/components/filterbar/filters/TextFilter';
import LocationFilter from '@/components/filterbar/filters/LocationFilter';
import AssetTypeFilter from '@/components/filterbar/filters/AssetTypeFilter';
import NumberFilter from '@/components/filterbar/filters/NumberFilter';
import { ColumnID } from '@/components/grid/column-ids';
import { AssetsGrid } from '../components/AssetsGrid';
import { useUser } from '@/modules/users/contexts/UserContext';
import { useLayoutActions } from '@/providers/layout-actions-provider/LayoutActionsProvider';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { useTranslation } from '@/lib';
import ChangeAssetTypeModal from '@/modules/locations/components/ChangeAssetTypeModal';

const ASSET_FETCH_LIMIT = 1000;

export const AssetsPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRows, setSelectedRows] = useState<AssetModel[]>([]);
  const [isMoveAssetModalOpen, setIsMoveAssetModalOpen] = useState(false);
  const [isChangeAssetTypeModalOpen, setIsChangeAssetTypeModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [clearGridSelection, setClearGridSelection] = useState<(() => void) | null>(null);
  const { t } = useTranslation();
  const { hasPermission } = useUser();
  const { setActions } = useLayoutActions();

  const { setFiltersToUrl, filters } = useQueryParamsFilters(
    {
      pageNumber: searchParams.get('pageNumber') ? Number.parseInt(searchParams.get('pageNumber')!) : 1,
    },
    CacheKey.ASSET_FILTERS,
  );

  const {
    data: assetData,
    isLoading: assetIsLoading,
    isError: assetIsError,
    fetchData: fetchAssets,
    setApiCallArg,
  } = useApiCall<PagePaginationResultDto<AssetModel>>(() =>
    assetService.get(
      {
        searchText: filters.current.searchText,
        locationId: filters.current.locationId,
        assetTypeId: filters.current.assetTypeId,
        stayTimeInDays: filters.current.stayTimeInDays,
      },
      {
        page: filters.current.pageNumber,
        limit: ASSET_FETCH_LIMIT,
        order: filters.current.sortDirection,
        sort: filters.current.sortOption,
      },
    ),
  );

  const { exportToExcel } = useExportToExcel();

  const handleSearch = (filterValues: FilterValues, page?: number) => {
    setFiltersToUrl({
      ...filterValues,
      pageNumber: page ?? filters.current.pageNumber,
    });

    clearSelection();

    setApiCallArg(() =>
      assetService.get(
        {
          searchText: filterValues.searchText,
          locationId: filterValues.locationId,
          assetTypeId: filterValues.assetTypeId,
          stayTimeInDays: filterValues.stayTimeInDays,
        },
        {
          page: filterValues.pageNumber,
          limit: ASSET_FETCH_LIMIT,
          order: filterValues.sortDirection,
          sort: filterValues.sortOption,
        },
      ),
    );
  };

  const onGridReady = (event: GridReadyEvent<AssetModel>) => {
    // Apply initial sort state
    if (filters.current.sortOption && filters.current.sortDirection) {
      let columnId: ColumnID | undefined;

      switch (filters.current.sortOption) {
        case AssetSortOption.FIRST_EVENT_DATE:
          columnId = ColumnID.FIRST_EVENT_DATE;
          break;
        case AssetSortOption.LAST_EVENT_DATE:
          columnId = ColumnID.LAST_EVENT_DATE;
          break;
        case AssetSortOption.LOCATION_NAME:
          columnId = ColumnID.LOCATION_NAME;
          break;
        case AssetSortOption.ASSET_TYPE_NAME:
          columnId = ColumnID.ASSET_TYPE_NAME;
          break;
        case AssetSortOption.ASSET_CODE:
          columnId = ColumnID.ASSET_CODE;
          break;
        case AssetSortOption.LOCATION_ENTER_DATE:
          columnId = ColumnID.STAYTIME;
          break;
        default:
          break;
      }

      if (columnId) {
        event.api.applyColumnState({
          state: [
            {
              colId: columnId,
              sort: filters.current.sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
            },
          ],
          defaultState: { sort: null },
        });
      }
    }
  };

  const onSortChanged = (event: SortChangedEvent<AssetModel>) => {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns.find((col) => col.getSort());
      if (!changedColumn) {
        handleSearch(
          {
            ...filters.current,
            sortOption: undefined,
            sortDirection: undefined,
          },
          1,
        );
        return;
      }

      const changedColumnId = changedColumn.getColId();

      let sortOption: AssetSortOption | undefined;
      switch (changedColumnId) {
        case ColumnID.FIRST_EVENT_DATE:
          sortOption = AssetSortOption.FIRST_EVENT_DATE;
          break;
        case ColumnID.LAST_EVENT_DATE:
          sortOption = AssetSortOption.LAST_EVENT_DATE;
          break;
        case ColumnID.LOCATION_NAME:
          sortOption = AssetSortOption.LOCATION_NAME;
          break;
        case ColumnID.ASSET_TYPE_NAME:
          sortOption = AssetSortOption.ASSET_TYPE_NAME;
          break;
        case ColumnID.ASSET_CODE:
          sortOption = AssetSortOption.ASSET_CODE;
          break;
        case ColumnID.STAYTIME:
          sortOption = AssetSortOption.LOCATION_ENTER_DATE;
          break;
        default:
          break;
      }

      let sortOrder: PageSortOrder | undefined;
      switch (changedColumn.getSort()) {
        case 'asc':
          sortOrder = PageSortOrder.ASC;
          break;
        case 'desc':
          sortOrder = PageSortOrder.DESC;
          break;
        default:
          sortOption = undefined;
          break;
      }

      handleSearch(
        {
          ...filters.current,
          sortOption,
          sortDirection: sortOrder,
        },
        1,
      );
    }
  };

  async function onExportToExcelClicked(gridApi: GridApi<AssetModel> | undefined) {
    if (gridApi) {
      const data: AssetModel[] = [];
      const dataPage1 = await assetService.get(
        {
          searchText: filters.current.searchText,
          locationId: filters.current.locationId,
          assetTypeId: filters.current.assetTypeId,
          stayTimeInDays: filters.current.stayTimeInDays,
        },
        {
          page: 1,
          limit: ASSET_FETCH_LIMIT,
          order: filters.current.sortDirection ?? PageSortOrder.ASC,
          sort: filters.current.sortOption,
        },
      );

      if (dataPage1.isSuccess) {
        data.push(...dataPage1.payload.data);
      }

      // Fetch additional pages if needed
      for (let i = 2; i <= (dataPage1.payload.totalPages || 1); i++) {
        const dataPage = await assetService.get(
          {
            searchText: filters.current.searchText,
            locationId: filters.current.locationId,
            assetTypeId: filters.current.assetTypeId,
            stayTimeInDays: filters.current.stayTimeInDays,
          },
          {
            page: i,
            limit: ASSET_FETCH_LIMIT,
            order: filters.current.sortDirection ?? PageSortOrder.ASC,
            sort: filters.current.sortOption,
          },
        );

        if (dataPage.isSuccess) {
          data.push(...dataPage.payload.data);
        }
      }

      if (data.length > 0) {
        // Calculate max count of each tracker type (same logic as grid)
        const trackerCounts = new Map<TrackerType, number>([
          [TrackerType.Barcode, 0],
          [TrackerType.RFID, 0],
          [TrackerType.BLE, 0],
          [TrackerType.Sigfox, 0],
        ]);

        data.forEach((asset) => {
          asset.trackers.forEach((tracker) => {
            const type = tracker.type;
            trackerCounts.set(type, Math.max(trackerCounts.get(type) || 0, asset.trackers.filter((t) => t.type === type).length));
          });
        });

        // Sort tracker types alphabetically by their string names for consistent ordering
        const sortedTrackerTypes = Array.from(trackerCounts.keys()).sort((a, b) => {
          const aStr = trackerTypeToString(a, t);
          const bStr = trackerTypeToString(b, t);
          return aStr.localeCompare(bStr);
        });

        exportToExcel(
          data.map((asset) => {
            const baseData = {
              code: asset.code,
              type: asset.assetType.name,
              location: asset.location?.name,
              firstEventDate: asset.firstEventDate,
              lastEventDate: asset.lastEventDate,
              stayTime: asset.locationEnteredDate ? dayjs().diff(dayjs(asset.locationEnteredDate), 'days') : undefined,
            };

            // Add tracker columns in alphabetical order by type
            const trackerData: Record<string, string> = {};
            sortedTrackerTypes.forEach((type) => {
              const count = trackerCounts.get(type) || 0;
              const trackersOfType = asset.trackers.filter((t) => t.type === type);

              for (let i = 0; i < count; i++) {
                const tracker = trackersOfType[i];
                let value = '';

                if (tracker) {
                  switch (tracker.type) {
                    case TrackerType.Barcode:
                      value = tracker.barcode ?? '';
                      break;
                    case TrackerType.RFID:
                      value = tracker.epc ?? '';
                      break;
                    case TrackerType.Sigfox:
                    case TrackerType.BLE:
                    case TrackerType.LTE:
                      value = tracker.deviceId ?? '';
                      break;
                  }
                }

                trackerData[`${trackerTypeToString(type, t)} #${i + 1}`] = value;
              }
            });

            return { ...baseData, ...trackerData };
          }),
          `assets_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
        );
      }
    }
  }

  const onRowDoubleClicked = (assetId: string) => {
    navigate(`../${assetId}`);
  };

  // Asset moving logic
  const onMoveSelectedAssetsClicked = () => {
    setIsMoveAssetModalOpen(true);
  };

  const onMoveSelectedAssetsClosed = () => {
    setIsMoveAssetModalOpen(false);
    clearSelection();
  };

  const onChangeAssetTypeClicked = () => {
    setIsChangeAssetTypeModalOpen(true);
  };

  const onChangeAssetTypeClosed = () => {
    setIsChangeAssetTypeModalOpen(false);
    clearSelection();
  };

  const clearSelection = () => {
    setSelectedRows([]);
    if (clearGridSelection) {
      clearGridSelection();
    }
  };

  const onAssetsMovedByGrid = () => {
    fetchAssets();
  };

  const onAssetTypeChangedByGrid = () => {
    fetchAssets();
  };

  const onDeleteSelectedAssets = async () => {
    setIsDeleting(true);
    try {
      const response = await assetService.bulkDelete({ assetIds: selectedRows.map((asset) => asset.id) });

      if (response.isSuccess) {
        toast.success(t('asset_deleted'));
        fetchAssets();
        clearSelection();
      } else {
        toast.error(t('error_deleting_asset'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearSelectionRef = useCallback((clearSelectionFn: () => void) => {
    setClearGridSelection(() => clearSelectionFn);
  }, []);

  const onPageChanged = (event: React.ChangeEvent<unknown>, page: number) => {
    handleSearch(
      {
        ...filters.current,
        pageNumber: page,
      },
      page,
    );
  };

  const hasSelectedRows = selectedRows.length > 0;

  function onCreateLocationClicked() {
    navigate({ pathname: '../create' });
  }

  useEffect(() => {
    if (hasPermission(Permission.LOCATIONS_EDIT)) {
      setActions(
        <Button variant="contained" onClick={onCreateLocationClicked}>
          {t('create')}
        </Button>,
      );
    }

    return () => {
      setActions('');
    };
  }, []);

  const filterComponents = [
    { label: t('search'), filterName: FilterFieldName.searchText, filter: <TextFilter label={t('search')} />, embedded: true },
    {
      label: t('location'),
      filterName: FilterFieldName.locationId,
      filter: <LocationFilter label={t('location')} />,
      searchOnChange: true,
    },
    { label: t('asset_type'), filterName: FilterFieldName.assetTypeId, filter: <AssetTypeFilter label={t('asset_type')} /> },
    {
      label: t('minimum_stay_time_days'),
      filterName: FilterFieldName.stayTimeInDays,
      filter: <NumberFilter label={t('minimum_stay_time_days')} filterFieldName={FilterFieldName.stayTimeInDays} />,
    },
  ];

  return (
    <TabbedPageLayout>
      <TabbedPageLayoutBody>
        <div className="flex h-full grow flex-col ">
          {assetIsError ? (
            <ErrorLoadingDataAlert />
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <FilterBar
                  onSearch={handleSearch}
                  initialFilterValues={filters.current}
                  isLoading={assetIsLoading}
                  showAsPopover
                  filterComponents={filterComponents}
                ></FilterBar>
              </div>
              <AssetsGrid
                data={assetData?.data}
                isLoading={assetIsLoading}
                onSelectionChanged={setSelectedRows}
                onRowDoubleClicked={onRowDoubleClicked}
                onExportToExcelClicked={onExportToExcelClicked}
                onSortChanged={onSortChanged}
                onGridReady={onGridReady}
                onClearSelectionRef={handleClearSelectionRef}
              />
              <div className="flex items-center gap-x-4">
                <PaginationControls
                  isLoading={assetIsLoading}
                  totalPageCount={assetData?.totalPages ?? 1}
                  currentPage={filters.current.pageNumber ?? 1}
                  totalElements={assetData?.totalElements ?? 0}
                  onChange={onPageChanged}
                />
                <PagedResultDataText data={assetData} name={t('assets')} />
              </div>
            </>
          )}
        </div>
        <ActionBar visible={hasSelectedRows}>
          <Button variant="outlined" color="primary" onClick={clearSelection}>
            {t('clear_selection')}
          </Button>
          <Button variant="contained" onClick={onMoveSelectedAssetsClicked}>
            {t('move_assets')}
          </Button>
          <Button variant="contained" onClick={onChangeAssetTypeClicked}>
            {t('change_asset_type')}
          </Button>
          <ActionBarDeleteButton
            isVisible
            isLoading={isDeleting}
            deleteConfirmationQuestion={t('delete_confirmation_question', { count: selectedRows.length })}
            onDeleteConfirmed={onDeleteSelectedAssets}
          />
        </ActionBar>
        <MoveAssetsToLocationModal
          selectedAssetIds={selectedRows.map((asset) => asset.id)}
          isOpen={isMoveAssetModalOpen}
          onClose={onMoveSelectedAssetsClosed}
          onAssetsMoved={onAssetsMovedByGrid}
        />
        <ChangeAssetTypeModal
          selectedAssetIds={selectedRows.map((asset) => asset.id)}
          isOpen={isChangeAssetTypeModalOpen}
          onClose={onChangeAssetTypeClosed}
          onAssetsMoved={onAssetTypeChangedByGrid}
        />
      </TabbedPageLayoutBody>
    </TabbedPageLayout>
  );
};
